import { ShieldCheck, Mail, FileCheck, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/Footer';

const CompliancePage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <Link
            to="/"
            className="text-2xl font-bold text-capledger-teal hover:opacity-80 transition-opacity"
          >
            CapLedger
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="space-y-8">
          {/* Title Section */}
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-foreground">CapLedger Compliance Statement</h1>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div>
                <strong>Effective Date:</strong> October 2025
              </div>
              <div>
                <strong>Entity:</strong> Spesvest (Pty) Ltd (Reg. No. 2016/197925/07)
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Address:</strong> Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
              Africa, 7600
            </div>
            <div className="text-sm text-muted-foreground">
              <strong>Email:</strong>{' '}
              <a
                href="mailto:compliance@capledger.co.za"
                className="text-capledger-teal hover:underline"
              >
                compliance@capledger.co.za
              </a>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                1. Our Compliance Commitment
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Spesvest (Pty) Ltd is committed to maintaining the highest standards of compliance,
                governance, and ethical conduct in all operations of CapLedger. We design and
                operate the platform according to recognised information security and regulatory
                frameworks to ensure data integrity, confidentiality, and lawful processing.
              </p>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                2. Regulatory Framework Alignment
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                CapLedger's architecture and policies are built to align with:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  <thead>
                    <tr className="bg-muted">
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Framework / Law
                      </th>
                      <th className="border border-border px-4 py-3 text-left font-semibold">
                        Relevance
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        POPIA (South Africa)
                      </td>
                      <td className="border border-border px-4 py-3">
                        Protection of personal and corporate information for all South African
                        users.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">GDPR (EU/UK)</td>
                      <td className="border border-border px-4 py-3">
                        Data protection, consent, and cross-border data flow for international
                        users.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        Companies Act 71 of 2008
                      </td>
                      <td className="border border-border px-4 py-3">
                        Compliance with South African corporate record-keeping and shareholder
                        disclosure rules.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        ISO/IEC 27001 Principles
                      </td>
                      <td className="border border-border px-4 py-3">
                        Information Security Management — encryption, access control, and risk
                        mitigation.
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-border px-4 py-3 font-semibold">
                        SOC 2 Type I/II Controls
                      </td>
                      <td className="border border-border px-4 py-3">
                        Governance alignment for system security, availability, and confidentiality.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                3. Governance and Accountability
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  <strong>Data Controller:</strong> Customers using CapLedger (they determine the
                  purpose and means of processing).
                </li>
                <li>
                  <strong>Data Processor:</strong> Spesvest (Pty) Ltd, which operates CapLedger
                  securely on behalf of customers.
                </li>
                <li>
                  <strong>Compliance Officer:</strong> A designated officer oversees data protection
                  and internal audit obligations under POPIA Section 55 and GDPR Article 37.
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">4. Security Practices</h2>
              <p className="text-muted-foreground leading-relaxed">
                We maintain layered security controls, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>End-to-end encryption (TLS 1.3 and AES-256)</li>
                <li>Multi-region backups and redundancy</li>
                <li>Immutable audit trails and event logging</li>
                <li>Role-based access control (RBAC)</li>
                <li>Continuous vulnerability scanning and penetration testing</li>
                <li>Incident response and breach notification protocol</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Independent audits and policy reviews are conducted annually to validate compliance
                posture.
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                5. Data Retention and Deletion
              </h2>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>
                  Customer data is retained only as long as necessary to fulfil contractual and
                  legal obligations.
                </li>
                <li>
                  On account closure or termination, all associated data is deleted or anonymised
                  within 30 days, unless retention is required by law (e.g., Companies Act or tax
                  regulations).
                </li>
                <li>Backups are purged according to defined retention schedules.</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">
                6. Cross-Border Data Transfers
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                For international clients, data transfers between South Africa, the EU, or other
                regions comply with:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>GDPR Standard Contractual Clauses (SCCs)</li>
                <li>POPIA Section 72 transborder requirements</li>
                <li>Adequacy decisions or approved data-transfer mechanisms where applicable</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">7. Subprocessors</h2>
              <p className="text-muted-foreground leading-relaxed">
                CapLedger uses a limited set of subprocessors for infrastructure, analytics, and
                operational support. All subprocessors are contractually bound to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>Maintain equivalent data protection standards</li>
                <li>Process data only under instruction</li>
                <li>Prohibit onward transfer without authorisation</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                An up-to-date list of subprocessors can be requested at{' '}
                <a
                  href="mailto:compliance@capledger.co.za"
                  className="text-capledger-teal hover:underline"
                >
                  compliance@capledger.co.za
                </a>
                .
              </p>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">8. Audit and Certification</h2>
              <p className="text-muted-foreground leading-relaxed">
                Spesvest maintains internal compliance documentation aligned to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>ISO 27001 information security objectives</li>
                <li>SOC 2 (security, availability, confidentiality) control framework</li>
                <li>Regular third-party vulnerability assessments</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed">
                Evidence of compliance (policies, audit logs, and control summaries) is available to
                enterprise clients under NDA.
              </p>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">9. Incident Management</h2>
              <p className="text-muted-foreground leading-relaxed">
                In case of a data breach or system compromise:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
                <li>The incident is immediately contained and investigated.</li>
                <li>Affected clients are notified without undue delay.</li>
                <li>Regulatory authorities are informed if required by law.</li>
                <li>Post-incident reviews are conducted to strengthen controls.</li>
              </ul>
            </section>

            {/* Section 10 - Contact & Requests */}
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold text-foreground">10. Reporting & Requests</h2>
              <p className="text-muted-foreground leading-relaxed">
                For compliance, audit, or due diligence inquiries:
              </p>
              <div className="bg-muted p-6 rounded-lg space-y-3">
                <a
                  href="mailto:compliance@capledger.co.za"
                  className="flex items-center gap-2 text-capledger-teal hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  compliance@capledger.co.za
                </a>
                <p className="text-sm text-muted-foreground">
                  Spesvest (Pty) Ltd, Unit C, Doornbosch Centre, Strand Rd, Stellenbosch, South
                  Africa, 7600
                </p>
              </div>

              <div className="bg-muted/50 border-l-4 border-capledger-teal p-4 rounded">
                <p className="text-sm font-semibold text-foreground mb-2">
                  Enterprise clients may also request:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-4">
                  <li>Our latest Compliance Overview Pack</li>
                  <li>A signed Data Processing Agreement (DPA)</li>
                  <li>Subprocessor list and security summary sheets</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <ShieldCheck className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <span className="text-sm text-muted-foreground">ISO 27001 Aligned</span>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <FileCheck className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <span className="text-sm text-muted-foreground">SOC 2 Controls</span>
            </div>
            <div className="flex items-center gap-3 p-6 bg-muted/50 rounded-lg border border-border">
              <AlertCircle className="h-6 w-6 text-capledger-teal flex-shrink-0" />
              <span className="text-sm text-muted-foreground">Annual Audits</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CompliancePage;
